import React from 'react';
import { find, map } from 'lodash';
import { Link } from 'react-router-dom';
import { routes } from '../../../data/constants';
import CampaignStatusBadge from '../../components/CampaignStatusBadge';

const AdvertisementRow = ({ advertisement, campaign, languages, isActive, statusTitle, canEdit, selected, onToggleSelect, onEdit, onDelete, showPrimary, onTogglePrimary, primarySaving }) => {
  const description = advertisement.description && advertisement.description !== '' ? advertisement.description : '<no description>';
  const isPrimary = showPrimary && advertisement.isPrimary;

  return (
    <li className={`builder-flex builder-items-start builder-gap-3 builder-px-5 builder-py-3 builder-border-t builder-border-gray-100 ${isPrimary ? 'builder-bg-yellow-50' : ''}`}>
      <input type='checkbox' className='builder-mt-1.5 builder-cursor-pointer' checked={selected} disabled={!canEdit} onChange={onToggleSelect} aria-label={`Select ${description}`} data-testid={`advertisement-select-${advertisement.id}`} />

      <div className='builder-flex builder-flex-col builder-flex-1 builder-min-w-0 builder-gap-1.5'>
        <div className='builder-flex builder-flex-wrap builder-items-center builder-gap-x-3 builder-gap-y-1'>
          {showPrimary &&
            (isPrimary ? (
              <button
                type='button'
                disabled={!canEdit || primarySaving}
                onClick={onTogglePrimary}
                className='builder-inline-flex builder-items-center builder-px-2 builder-py-0.5 builder-rounded-full builder-border builder-border-yellow-300 builder-bg-yellow-100 builder-text-yellow-800 builder-text-sm builder-font-semibold'
                title='Shown first in this group. Click to remove.'
                data-testid={`advertisement-primary-${advertisement.id}`}
              >
                <i className='fas fa-star builder-mr-1' style={{ fontSize: 11 }} />
                Primary
              </button>
            ) : (
              <button
                type='button'
                disabled={!canEdit || primarySaving}
                onClick={onTogglePrimary}
                className='builder-inline-flex builder-items-center builder-px-2 builder-py-0.5 builder-rounded-full builder-border builder-border-gray-200 builder-bg-white builder-text-gray-600 builder-text-sm hover:builder-border-gray-400'
                title='Show this advertisement first in this group'
                data-testid={`advertisement-primary-${advertisement.id}`}
              >
                <i className='fal fa-star builder-mr-1' style={{ fontSize: 11 }} />
                Make primary
              </button>
            ))}
          <span className='builder-font-semibold builder-text-black'>{description}</span>
          <span
            className={`builder-inline-flex builder-items-center builder-px-2 builder-py-0.5 builder-rounded-full builder-text-sm builder-font-semibold ${
              isActive ? 'builder-bg-green-100 builder-text-green-700' : 'builder-bg-gray-100 builder-text-gray-600'
            }`}
            title={statusTitle}
          >
            <span className={`builder-w-2 builder-h-2 builder-rounded-full builder-mr-1.5 ${isActive ? 'builder-bg-green-500' : 'builder-bg-gray-400'}`} />
            {isActive ? 'Shown' : 'Not shown'}
          </span>
        </div>

        <div className='builder-flex builder-flex-wrap builder-items-center builder-gap-x-5 builder-gap-y-1 builder-text-sm builder-text-gray-600'>
          <span className='builder-inline-flex builder-gap-1'>
            {map(languages, (language) => {
              const hasContent = !!find(advertisement.content, (content) => content.culture === language);
              return (
                <span
                  key={language}
                  title={hasContent ? `Content available for ${language.toUpperCase()}` : `No content for ${language.toUpperCase()}`}
                  className={`builder-px-1.5 builder-rounded builder-text-sm builder-font-bold builder-uppercase ${hasContent ? 'builder-bg-green-100 builder-text-green-800' : 'builder-bg-gray-100 builder-text-gray-500 builder-line-through'}`}
                >
                  {language}
                </span>
              );
            })}
          </span>

          {campaign ? (
            <span className='builder-inline-flex builder-flex-wrap builder-items-center builder-gap-x-2'>
              <Link to={`${routes.CAMPAIGN_DETAIL}/${campaign.id}`} className='builder-inline-flex builder-items-center builder-text-black hover:builder-underline' title='Open campaign'>
                {campaign.name}
                <i className='fal fa-external-link-alt builder-ml-1.5' style={{ fontSize: 11 }} />
              </Link>
              <span className='builder-whitespace-nowrap'>
                {campaign.start} - {campaign.stop}
              </span>
              <CampaignStatusBadge campaign={campaign} />
            </span>
          ) : advertisement.campaignId ? (
            <span className='builder-text-yellow-700' title='The linked campaign is not available in this environment, the advertisement is still only shown during that campaign'>
              Campaign not available
            </span>
          ) : (
            <span>No campaign</span>
          )}

          {Array.isArray(advertisement.filters) && advertisement.filters.length > 0 ? (
            <span className='builder-inline-flex builder-flex-wrap builder-gap-1'>
              {map(advertisement.filters, (adFilter, index) => (
                <span key={`${adFilter.type}-${adFilter.value}-${index}`} className='builder-px-2 builder-rounded builder-bg-gray-100 builder-text-gray-700'>
                  {adFilter.value ? `${adFilter.type}: ${adFilter.value}` : adFilter.type}
                </span>
              ))}
            </span>
          ) : (
            <span>No filters</span>
          )}
        </div>
      </div>

      <div className='builder-flex builder-items-center builder-mt-0.5 builder-whitespace-nowrap'>
        {advertisement.locked && (
          <button type='button' className='builder-flex builder-items-center' onClick={onEdit} title='Synced advertisements can only be changed in the source builder'>
            <i className='fal fa-lock builder-mr-1' style={{ fontSize: 12 }} />
            Synced from {advertisement.source} · View
          </button>
        )}
        {!advertisement.locked && canEdit && (
          <React.Fragment>
            <button type='button' className='builder-flex builder-items-center builder-mr-3 builder-pr-3 builder-border-r builder-border-gray-300' onClick={onEdit}>
              Edit <i className='fal fa-pencil-alt builder-ml-1' style={{ fontSize: 12 }} />
            </button>
            <button type='button' className='builder-flex builder-items-center builder-text-red-700' onClick={onDelete}>
              Delete <i className='fal fa-trash-alt builder-ml-1' style={{ fontSize: 12 }} />
            </button>
          </React.Fragment>
        )}
        {!advertisement.locked && !canEdit && <span className='builder-text-sm builder-font-bold'>No actions allowed in draft environment</span>}
      </div>
    </li>
  );
};

export default AdvertisementRow;
